import React from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';

/**
 * Props for the StatsCard component.
 */
export interface StatsCardProps {
  /** The title displayed in the card header */
  title: string;
  /** The main value to display prominently */
  value: string | number;
  /** Optional description text displayed below the value */
  description?: string;
}

/**
 * StatsCard Component
 *
 * A card component for displaying statistics on the dashboard.
 * Uses Cloudscape Container component for consistent styling.
 *
 * Features:
 * - Displays a title in the card header
 * - Shows the value prominently in large text
 * - Optionally displays a description as secondary text
 * - Consistent styling with Cloudscape Design System
 *
 * @example
 * ```tsx
 * <StatsCard
 *   title="Average Score"
 *   value={85}
 *   description="Based on 10 sessions"
 * />
 * ```
 *
 * @validates Requirements 9.1 - The dashboard shall display stat cards showing Average Score, Sessions Completed, and Total Sessions
 */
export function StatsCard({ title, value, description }: StatsCardProps): React.ReactElement {
  return (
    <Container
      data-testid="stats-card"
      header={
        <Header variant="h3" data-testid="stats-card-title">
          {title}
        </Header>
      }
    >
      <Box
        data-testid="stats-card-value"
        variant="h1"
        textAlign="center"
        padding={{ vertical: 's' }}
      >
        {value}
      </Box>
      {description && (
        <Box
          data-testid="stats-card-description"
          variant="small"
          color="text-body-secondary"
          textAlign="center"
        >
          {description}
        </Box>
      )}
    </Container>
  );
}

export default StatsCard;
